import { ButtonTheme, FormFieldType, FormSubmitAction, FormWidgetLayout, IFormFieldData, IFormFieldOption, IFormSubmitData } from "@pageBuilder/typings";
import { StringUtil } from "../../../utils/string-util";
import { Base64Util } from "../../../utils/base64-util";

export class FormFieldRenderer {

    private static readonly MAX_TEXT_LENGTH = 500;
    private static readonly MAX_EMAIL_LENGTH = 254;
    private static readonly MAX_MOBILE_LENGTH = 20;

    public static ensureHidden(form: HTMLElement, name: string, value: string): void {
        const field = Array.from(form.querySelectorAll<HTMLInputElement>(`[type=hidden]`)).find(x => x.name === name);

        if (field) {
            field.value = value;
        } else {
            form.appendChild(FormFieldRenderer.createHidden(name, value));
        }
    }

    public static createHidden(name: string, value: string): HTMLInputElement {
        const code = document.createElement("input");
        code.type = "hidden";
        code.name = name;
        code.value = value;
        return code;
    }

    public static createFieldsWrapper(): HTMLDivElement {
        const fieldsWrapper = document.createElement("div");
        fieldsWrapper.classList.add("fields-wrapper");

        return fieldsWrapper;
    }

    public static createField(data: IFormFieldData, layout: FormWidgetLayout, labelStyle: string, isMobile: boolean): HTMLElement {
        const field = document.createElement("div");

        field.classList.add("field");

        if (layout === "column" || isMobile) {
            const label = document.createElement("label");
            label.classList.toggle("field-label", true);
            label.setAttribute("for", `f_${data.id}`);
            if (labelStyle === "inline" && data.type !== "RadioButton") {
                label.classList.add("sr-only");
            }
            label.innerText = data.required ? data.caption + "*" : data.caption;
            field.appendChild(label);
        }

        const control = this.createControl(data, labelStyle);
        field.appendChild(control);

        if (data.description && data.description.trim().length > 0) {
            const description = document.createElement("div");
            description.classList.add("field-description");
            description.innerText = data.description;
            field.appendChild(description);
            field.dataset.description = data?.description ?? "";
        }

        field.dataset.id = data.id;
        field.dataset.type = data.type;
        field.dataset.caption = data.caption;

        if (data.required) {
            field.dataset.required = "true";
        }

        if (data.options && data.options.length > 0) {
            field.dataset.options = Base64Util.serialize(data.options) || "";
        } else {
            field.dataset.options = "";
        }

        return field;
    }

    public static parseField(field: HTMLElement): IFormFieldData {
        const result: IFormFieldData = {
            id: field.dataset.id ?? "",
            type: field.dataset.type as FormFieldType ?? "Text",
            caption: field.dataset.caption ?? "",
            description: field.dataset.description ?? "",
            required: !!field.dataset.required,
            options: undefined
        };

        if (!StringUtil.isNullOrWhiteSpace(field.dataset.options)) {
            try {
                result.options = Base64Util.deserialize(field.dataset.options) as IFormFieldOption[];
            } catch {
                // do nothing
            }
        }

        return result;
    }

    public static createActions(form: HTMLFormElement, fieldsWrapper: HTMLDivElement, data: IFormSubmitData, theme: ButtonTheme): void {
        fieldsWrapper.querySelectorAll(".actions").forEach(x => x.remove());

        const actions = document.createElement("div");
        actions.classList.add("actions");

        const button = document.createElement("button");
        button.type = "submit";
        button.classList.add("submit");
        button.classList.toggle("secondary", theme === "secondary");
        button.setAttribute("tabindex", "-1");
        button.innerText = (data.text && data.text.trim().length > 0) ? data.text : "Submit";
        actions.appendChild(button);

        form.dataset.actionType = data.action.type;
        if (data.action.type === "message") {
            form.dataset.message = data.action.message;
        } else if (data.action.type === "redirect") {
            form.dataset.redirectUrl = data.action.url;
        }

        fieldsWrapper.appendChild(actions);
    }

    public static parseActions(form?: HTMLFormElement | null): IFormSubmitData {
        const actions = form?.querySelector<HTMLElement>(".actions");

        const button = actions?.querySelector("[type=submit]");
        const submitAction: FormSubmitAction = form?.dataset.actionType === "message" ?
            { type: "message", message: form.dataset.message ?? "" } :
            { type: "redirect", url: form?.dataset.redirectUrl ?? "" };

        return {
            text: button?.innerHTML ?? "",
            action: submitAction
        };
    }

    private static createControl(data: IFormFieldData, labelStyle: string): HTMLElement {
        switch (data.type) {
            case "Text":
                return this.createInputElement("text", data, labelStyle);
            case "Email":
                return this.createInputElement("email", data, labelStyle);
            case "Phone":
                return this.createInputElement("tel", data, labelStyle);
            case "File":
                return this.createInputElement("file", data, labelStyle);
            case "Number":
                return this.createInputElement("number", data, labelStyle);
            case "Password":
                return this.createInputElement("password", data, labelStyle);
            case "TextArea":
                return this.createTextArea(data, labelStyle);
            case "RadioButton":
                return this.createRadioButtonsElement(data);
            default:
                const fallback = document.createElement("div");
                fallback.innerHTML = `Form field type "${data.type}" is not supported`;
                return fallback;
        }
    }

    private static createInputElement(type: string, data: IFormFieldData, labelStyle: string): HTMLElement {
        return this.createInput(type, data, labelStyle);
    }

    private static createInput(type: string, data: IFormFieldData, labelStyle: string): HTMLInputElement {
        const input = document.createElement("input");
        input.type = type;
        input.name = data.id;
        input.id = `f_${data.id}`;
        input.classList.add("control");
        input.setAttribute("tabindex", "-1");
        FormFieldRenderer.assignPlaceholder(input, data, labelStyle);
        FormFieldRenderer.setLimitation(type, input);
        return input;
    }

    private static setLimitation(type: string, input: HTMLElement): void {
        if (type === "text") {
            input.setAttribute("maxlength", FormFieldRenderer.MAX_TEXT_LENGTH.toString());
        } else if (type === "email") {
            input.setAttribute("maxlength", FormFieldRenderer.MAX_EMAIL_LENGTH.toString());
        } else if (type === "tel") {
            input.setAttribute("maxlength", FormFieldRenderer.MAX_MOBILE_LENGTH.toString());
        }
    }

    private static createTextArea(data: IFormFieldData, labelStyle: string): HTMLTextAreaElement {
        const input = document.createElement("textarea");
        input.name = data.id;
        input.id = `f_${data.id}`;
        input.classList.add("control");
        input.setAttribute("maxlength", FormFieldRenderer.MAX_TEXT_LENGTH.toString());
        input.setAttribute("tabindex", "-1");
        FormFieldRenderer.assignPlaceholder(input, data, labelStyle);
        return input;
    }

    private static assignPlaceholder(input: HTMLInputElement | HTMLTextAreaElement, data: IFormFieldData, labelStyle: string): void {
        if (labelStyle === "inline") {
            input.placeholder = data.required ? data.caption + "*" : data.caption;
        }
        if (data.required) {
            input.required = data.required;
        }
    }

    public static createLabels(fields: IFormFieldData[]): HTMLDivElement {
        const labelsWrapper = document.createElement("div");
        labelsWrapper.classList.add("labels-wrapper");
        fields.forEach(field => {
            const labelDiv = this.createRowLayoutLabel(field);
            labelsWrapper.appendChild(labelDiv);
        });

        return labelsWrapper;
    }

    private static createRowLayoutLabel(data: IFormFieldData): HTMLLabelElement {
        const label = document.createElement("label");
        label.setAttribute("for", `f_${data.id}`);
        label.innerText = data.required ? data.caption + "*" : data.caption;
        label.classList.add("row-label");

        return label;
    }

    public static createRadioButtonsElement(data: IFormFieldData): HTMLDivElement {
        const radioBtnList = document.createElement("div");
        radioBtnList.className = "radio-btn-list";

        data.options?.forEach((option: IFormFieldOption, index: number) => {
            const radio = this.createRadioElement(option, index, data.id);
            radioBtnList.appendChild(radio);
        });

        return radioBtnList;
    }

    private static createRadioElement(option: IFormFieldOption, index: number, fieldId: string): HTMLDivElement {
        const radioContent = document.createElement("label");
        radioContent.className = "radio-content";
        radioContent.appendChild(this.createRadioInputElement(option, index, fieldId));
        radioContent.appendChild(this.createRadioBtnElement(index, fieldId));
        radioContent.appendChild(this.createRadioLabelElement(option.caption ?? option.value));

        const radio = document.createElement("div");
        radio.className = "radio";
        radio.appendChild(radioContent);

        return radio;
    }

    private static createRadioInputElement(option: IFormFieldOption, index: number, fieldId: string): HTMLInputElement {
        const radioInput = document.createElement("input");
        radioInput.type = "radio";
        radioInput.name = fieldId;
        radioInput.id = `f_${fieldId}-option-${index}`;
        radioInput.value = option.value;
        radioInput.toggleAttribute("checked", index === 0);
        radioInput.setAttribute("tabindex", "-1");

        return radioInput;
    }

    private static createRadioBtnElement(index: number, fieldId: string): HTMLLabelElement {
        const radioBtn = document.createElement("label");
        radioBtn.className = "radio-btn";
        radioBtn.setAttribute("for", `f_${fieldId}-option-${index}`);

        return radioBtn;
    }

    private static createRadioLabelElement(text: string): HTMLSpanElement {
        const radioLabel = document.createElement("span");
        radioLabel.className = "radio-label";
        radioLabel.innerText = text;

        return radioLabel;
    }

}
